/**
 * Gingerbread house ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_christmas_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const gingerbreadHouseIdeasPack: ToilePack = {
    slug: 'gingerbread-house-ideas',
    category: 'season',
    name: 'Gingerbread house ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'classic', label: 'Classic', subject: 'a gingerbread house with icing snow and candy'},
        {key: 'cabin', label: 'Cabin', subject: 'a log cabin gingerbread house'},
        {key: 'village', label: 'Village', subject: 'a gingerbread village'},
    ],
    styles: [
        {key: 'counter', label: 'Kitchen counter', style: 'on a kitchen counter in soft daylight from camera-left, a bright balance with gentle shadow', palette: 'warm brown gingerbread, white royal icing, red and green candy', framing: 'three-quarter eye-level view, the whole build in frame'},
        {key: 'night', label: 'Glowing windows', style: 'at night with a warm glow inside its windows and dim blue light from behind, a low balance of dark room and golden windows', palette: 'amber window glow, white icing, deep blue shadow', framing: "low close view at the roofline's height"},
        {key: 'macro', label: 'Icing detail', style: 'a macro close-up of piped icing, candy and baked texture with shallow depth of field, daylight from above, a bright balance with soft shadow', palette: 'brown, white and candy colors', framing: 'tight crop on the roof and front'},
    ],
    directions: ['a castle', 'pastel candy', 'tiny houses'],
    directionHint: 'try: a castle',
    seo: {
        title: 'Gingerbread House Ideas: Classic, Cabin, Village',
        metaDescription: 'Nine photos of gingerbread houses: a classic house, a log cabin and a village, on a counter, glowing at night and in icing detail.',
        h1: 'Gingerbread house ideas: classic, cabin, village',
        tagline: 'Three builds, each on a kitchen counter, glowing at night and close up in icing and candy.',
        intro: 'Three gingerbread builds for a family to make: a classic house with icing snow and candy, a log cabin and a whole gingerbread village. Each is shown on a kitchen counter, with its windows glowing at night and in a close look at the piped icing and candy. Use them to choose a build before the baking starts. Type a twist, such as a castle or tiny houses, and the build is asked to follow it.',
        useCases: [
            'choosing a build before the baking starts',
            'a family decorating night',
            'a school or club holiday project',
            'a centerpiece for the dessert table',
        ],
        faq: [
            {q: 'What builds are in the pack?', a: 'A classic gingerbread house with icing snow and candy, a log cabin, and a gingerbread village.'},
            {q: 'Do the pictures show a recipe?', a: 'No. The pictures show finished builds and do not give a recipe or steps.'},
            {q: 'What do the three styles show?', a: 'Kitchen counter shows the build in daylight. Glowing windows shows it at night with light inside. Icing detail is a close look at the roof and front.'},
            {q: 'Can I ask for a different build?', a: 'Yes. Type a twist, such as pastel candy, and the page asks for a build that follows it.'},
        ],
    },
}
